import { useEffect, useRef, useState, type ReactNode, type UIEvent } from 'react';
import { visibleRowWindow } from './userListWindow';

const OVERSCAN = 8;

type UserListScrollerProps<T> = {
  items: T[];
  rowHeight: number;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
};

export function UserListScroller<T>({ items, rowHeight, getKey, renderItem }: UserListScrollerProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef(0);
  const pendingScrollRef = useRef(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const measure = () => setViewport(element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    element.scrollTop = 0;
    pendingScrollRef.current = 0;
    setScrollTop(0);
  }, [items]);

  useEffect(() => () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
  }, []);

  const { start, end } = visibleRowWindow(scrollTop, viewport, items.length, rowHeight, OVERSCAN);
  const slice = items.slice(start, end);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    pendingScrollRef.current = event.currentTarget.scrollTop;
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      setScrollTop(pendingScrollRef.current);
    });
  };

  return (
    <div
      ref={scrollerRef}
      data-dq-table-scroll=""
      className="dq-table-scroll min-h-0 flex-1 overflow-auto overscroll-contain pr-1"
      onScroll={onScroll}
    >
      <div style={{ height: items.length * rowHeight, position: 'relative' }}>
        <div style={{ position: 'absolute', top: start * rowHeight, left: 0, right: 0 }}>
          {slice.map((item) => (
            <div key={getKey(item)} style={{ height: rowHeight, paddingBottom: 12 }}>
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
